// Ayarlar sayfası - SMTP ayarları
import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { smtpService } from '../../services/smtpService';
import { backupService } from '../../services/backupService';
import toast from 'react-hot-toast';
import { 
  FiMail,
  FiSave,
  FiSend,
  FiEye,
  FiEyeOff,
  FiDatabase,
  FiUpload,
  FiDownload
} from 'react-icons/fi';

const Settings = () => {
  const { user, hasPermission } = useAuth();
  const [activeSection, setActiveSection] = useState('smtp');
  
  
  // Admin ve manager kullanıcılar erişebilir
  if (!hasPermission('manager')) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">Bu sayfaya erişim yetkiniz yok.</p>
        <p className="text-sm text-gray-500 mt-2">
          Kullanıcı tipi: {user?.userType || user?.user_type || 'Bilinmiyor'}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Debug: userType={user?.userType}, user_type={user?.user_type}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sayfa başlığı */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Settings
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          SMTP ve Backup ayarlarını yönetin
        </p>
      </div>

      {/* Bölüm seçim butonları */}
      <div className="card p-2">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setActiveSection('smtp')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeSection === 'smtp'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            <span className="inline-flex items-center space-x-2">
              <FiMail size={16} />
              <span>SMTP Ayarları</span>
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveSection('backup')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeSection === 'backup'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            <span className="inline-flex items-center space-x-2">
              <FiDatabase size={16} />
              <span>Backup</span>
            </span>
          </button>
        </div>
      </div>

      {/* Aktif bölüme göre ilgili component render edilir */}
      {activeSection === 'smtp' ? <SmtpSettings /> : <BackupSettings />}
    </div>
  );
};

// Backup ayarları bileşeni
const BackupSettings = () => {
  const [sqlFile, setSqlFile] = useState(null);
  const [jsonFiles, setJsonFiles] = useState({});
  const [isFullJsonRestore, setIsFullJsonRestore] = useState(false);

  const jsonDatasets = [
    { key: 'users', label: 'USERS.JSON' },
    { key: 'customers', label: 'CUSTOMERS.JSON' },
    { key: 'projects', label: 'PROJECTS.JSON' },
    { key: 'offers', label: 'OFFERS.JSON' },
    { key: 'meetings', label: 'MEETINGS.JSON' },
    { key: 'all', label: 'FULL_BACKUP.JSON' }
  ];

  const downloadBlob = (blob, fallbackFilename) => {
    // Backend'ten gelen blob içeriğini tarayıcıdan dosya olarak indirmek için kullanılır.
    const fileUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fallbackFilename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(fileUrl);
  };

  const ensureBlobIsNotHtml = async (response) => {
    const contentType = response?.headers?.['content-type'] || '';
    if (contentType.includes('text/html')) {
      throw new Error('API yerine HTML döndü. REACT_APP_API_URL ve Nginx /api proxy ayarını kontrol edin.');
    }

    const blob = response?.data;
    if (!blob || typeof blob.text !== 'function') {
      return;
    }

    const previewText = (await blob.slice(0, 300).text()).toLowerCase();
    if (previewText.includes('<!doctype html') || previewText.includes('<html')) {
      throw new Error('API yerine HTML döndü. Endpoint /api/backup yoluna ulaşamıyor.');
    }
  };

  const sqlExportMutation = useMutation(backupService.exportSqlBackup, {
    onSuccess: async (response) => {
      await ensureBlobIsNotHtml(response);
      downloadBlob(response.data, `aprocrm-backup-${Date.now()}.sql`);
      toast.success('SQL backup başarıyla indirildi');
    },
    onError: (error) => {
      toast.error(error?.message || error?.response?.data?.message || 'SQL backup indirilemedi');
    }
  });

  const sqlRestoreMutation = useMutation(backupService.restoreSqlBackup, {
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'SQL restore tamamlandı');
      setSqlFile(null);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'SQL restore başarısız');
    }
  });

  const jsonExportMutation = useMutation((dataset) => backupService.exportJsonBackup(dataset), {
    onSuccess: async (response, dataset) => {
      await ensureBlobIsNotHtml(response);
      const fileName = `${dataset}-${Date.now()}.json`;
      downloadBlob(response.data, fileName);
      toast.success(`${dataset.toUpperCase()} JSON export tamamlandı`);
    },
    onError: (error) => {
      toast.error(error?.message || error?.response?.data?.message || 'JSON export başarısız');
    }
  });

  const jsonRestoreMutation = useMutation(
    ({ dataset, file }) => backupService.restoreJsonBackup(dataset, file),
    {
      onSuccess: (response, variables) => {
        toast.success(response?.data?.message || `${variables.dataset.toUpperCase()} restore tamamlandı`);
        setJsonFiles((prev) => ({ ...prev, [variables.dataset]: null }));
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'JSON restore başarısız');
      }
    }
  );

  const handleSqlRestore = () => {
    if (!sqlFile) {
      toast.error('Lütfen restore için .sql dosyası seçin');
      return;
    }
    if (!window.confirm('Mevcut veriler değişebilir. SQL restore işlemini başlatmak istiyor musunuz?')) {
      return;
    }
    sqlRestoreMutation.mutate(sqlFile);
  };

  const handleJsonRestore = (dataset) => {
    const file = jsonFiles[dataset];
    if (!file) {
      toast.error('Lütfen restore için JSON dosyası seçin');
      return;
    }
    if (!window.confirm(`${dataset.toUpperCase()} dataseti geri yüklenecek. Devam edilsin mi?`)) {
      return;
    }
    jsonRestoreMutation.mutate({ dataset, file });
  };

  const handleFullJsonRestore = () => {
    const file = jsonFiles.all;
    if (!file) {
      toast.error('Lütfen FULL_BACKUP için JSON dosyası seçin');
      return;
    }
    if (!window.confirm('TÜM tablolar geri yüklenecek. Bu işlem mevcut verilerin üzerine yazabilir. Devam edilsin mi?')) {
      return;
    }
    setIsFullJsonRestore(true);
    jsonRestoreMutation.mutate(
      { dataset: 'all', file },
      {
        onSettled: () => setIsFullJsonRestore(false)
      }
    );
  };

  return (
    <div className="space-y-6">
      <div className="card p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          Backup
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          SQL dump ile tam veritabanı yedeği alın/geri yükleyin, ayrıca sayfa bazlı JSON export/import işlemleri yapın.
        </p>

        <div className="space-y-4">
          <div className="border rounded-lg p-3 dark:border-gray-700">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Database (SQL Dump)</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              phpMyAdmin export benzeri şekilde `.sql` dosyası üretir ve restore eder.
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => sqlExportMutation.mutate()}
                disabled={sqlExportMutation.isLoading}
                className="btn-primary inline-flex items-center space-x-2 !px-3 !py-2 text-sm"
              >
                <FiDownload size={16} />
                <span>{sqlExportMutation.isLoading ? 'SQL hazırlanıyor...' : 'SQL Backup'}</span>
              </button>
              <input
                type="file"
                accept=".sql"
                onChange={(e) => setSqlFile(e.target.files?.[0] || null)}
                className="input-field !py-2 text-sm flex-1 min-w-[220px]"
              />
              <button
                type="button"
                onClick={handleSqlRestore}
                disabled={sqlRestoreMutation.isLoading}
                className="btn-secondary inline-flex items-center space-x-2 !px-3 !py-2 text-sm"
              >
                <FiUpload size={16} />
                <span>{sqlRestoreMutation.isLoading ? 'Restore yapılıyor...' : 'SQL ile Restore Et'}</span>
              </button>
            </div>
          </div>

          <div className="border rounded-lg p-3 dark:border-gray-700">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-1">JSON Export / Restore</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Belirli modülleri JSON formatında yedekleyip gerektiğinde geri yükleyin.
            </p>

            {/* JSON satırları tablo gibi kompakt gösterilir */}
            <div className="overflow-x-auto">
              <div className="min-w-[820px]">
                <div className="grid grid-cols-12 gap-2 px-2 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                  <div className="col-span-3">Dosya</div>
                  <div className="col-span-3">Export</div>
                  <div className="col-span-4">Restore Dosyası</div>
                  <div className="col-span-2">Restore</div>
                </div>

              {jsonDatasets.map((datasetItem) => (
                  <div
                    key={datasetItem.key}
                    className="grid grid-cols-12 gap-2 items-center px-2 py-2 border-b last:border-b-0 dark:border-gray-700"
                  >
                    <div className="col-span-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {datasetItem.label}
                    </div>

                    <div className="col-span-3">
                    <button
                      type="button"
                      onClick={() => jsonExportMutation.mutate(datasetItem.key)}
                      disabled={jsonExportMutation.isLoading}
                        className="btn-primary inline-flex items-center space-x-2 !px-3 !py-2 text-sm"
                    >
                      <FiDownload size={16} />
                        <span>JSON Export</span>
                    </button>
                  </div>

                    <div className="col-span-4">
                    <input
                      type="file"
                      accept=".json,application/json"
                      onChange={(e) =>
                        setJsonFiles((prev) => ({
                          ...prev,
                          [datasetItem.key]: e.target.files?.[0] || null
                        }))
                      }
                        className="input-field !py-2 text-sm"
                    />
                    </div>

                    <div className="col-span-2">
                    {datasetItem.key === 'all' ? (
                      <button
                        type="button"
                        onClick={handleFullJsonRestore}
                        disabled={isFullJsonRestore}
                          className="btn-secondary inline-flex items-center space-x-2 !px-3 !py-2 text-sm"
                      >
                        <FiUpload size={16} />
                          <span>{isFullJsonRestore ? 'Bekleyin...' : 'Restore'}</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleJsonRestore(datasetItem.key)}
                        disabled={jsonRestoreMutation.isLoading}
                          className="btn-secondary inline-flex items-center space-x-2 !px-3 !py-2 text-sm"
                      >
                        <FiUpload size={16} />
                          <span>Restore</span>
                      </button>
                    )}
                    </div>
                  </div>
              ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// SMTP ayarları bileşeni
const SmtpSettings = () => {
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showTestModal, setShowTestModal] = useState(false);
  const [formData, setFormData] = useState({
    smtp_status: 0,
    smtp_host: '',
    smtp_port: '',
    smtp_security: 'TLS',
    smtp_config_mail: '',
    smtp_config_mail_password: '',
    smtp_sender_email: '',
    smtp_sender_name: '',
    smtp_sender_reply_address: '',
    smtp_cc_mail: '',
    smtp_bcc_mail: ''
  });

  // SMTP ayarlarını getir
  const { data: smtpSettings, isLoading } = useQuery(
    'smtpSettings',
    smtpService.getSmtpSettings,
    {
      onSuccess: (data) => {
        if (data?.data?.data) {
          const settings = data.data.data;
          setFormData(settings);
        }
      },
      onError: (error) => {
        toast.error('SMTP ayarları yüklenemedi: ' + error.message);
      }
    }
  );


  // SMTP ayarlarını güncelle
  const updateMutation = useMutation(
    smtpService.updateSmtpSettings,
    {
      onSuccess: () => {
        toast.success('SMTP ayarları başarıyla güncellendi');
        queryClient.invalidateQueries('smtpSettings');
      },
      onError: (error) => {
        toast.error('SMTP ayarları güncellenemedi: ' + error.message);
      }
    }
  );

  // SMTP test
  const testMutation = useMutation(
    smtpService.testSmtpConnection,
    {
      onSuccess: (data) => {
        toast.success(data.data.message);
        setShowTestModal(false);
        setTestEmail('');
      },
      onError: (error) => {
        toast.error('SMTP test başarısız: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleTest = () => {
    if (!testEmail) {
      toast.error('Test email adresi gerekli');
      return;
    }
    
    const testData = {
      ...formData,
      test_email: testEmail
    };
    
    testMutation.mutate(testData);
  };

  if (isLoading) {
    return <div className="text-center py-8">Yükleniyor...</div>;
  }

  return (
    <div className="card p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* SMTP Durumu */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              SMTP Durumu
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              SMTP mail gönderimini etkinleştirin veya devre dışı bırakın
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="smtp_status"
              checked={formData.smtp_status === 1}
              onChange={handleInputChange}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {/* SMTP Sunucu Ayarları */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              SMTP Sunucu *
            </label>
            <input
              type="text"
              name="smtp_host"
              value={formData.smtp_host}
              onChange={handleInputChange}
              className="input-field"
              placeholder="smtp.gmail.com"
              autoComplete="off"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Port *
            </label>
            <input
              type="text"
              name="smtp_port"
              value={formData.smtp_port}
              onChange={handleInputChange}
              className="input-field"
              placeholder="587"
              autoComplete="off"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Güvenlik
            </label>
            <select
              name="smtp_security"
              value={formData.smtp_security}
              onChange={handleInputChange}
              className="input-field"
            >
              <option value="TLS">TLS</option>
              <option value="SSL">SSL</option>
              <option value="NONE">Güvenlik Yok</option>
            </select>
          </div>
        </div>

        {/* Kimlik Doğrulama */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email Adresi *
            </label>
            <input
              type="email"
              name="smtp_config_mail"
              value={formData.smtp_config_mail}
              onChange={handleInputChange}
              className="input-field"
              placeholder="your-email@gmail.com"
              autoComplete="off"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Şifre
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="smtp_config_mail_password"
                value={formData.smtp_config_mail_password}
                onChange={handleInputChange}
                className="input-field pr-10"
                placeholder="Şifre girin (boş bırakırsanız mevcut şifre korunur)"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Gönderici Bilgileri */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Gönderici Email
            </label>
            <input
              type="email"
              name="smtp_sender_email"
              value={formData.smtp_sender_email}
              onChange={handleInputChange}
              className="input-field"
              placeholder="noreply@apro.com.tr"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Gönderici Adı
            </label>
            <input
              type="text"
              name="smtp_sender_name"
              value={formData.smtp_sender_name}
              onChange={handleInputChange}
              className="input-field"
              placeholder="APRO CRM"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Yanıt Adresi
            </label>
            <input
              type="email"
              name="smtp_sender_reply_address"
              value={formData.smtp_sender_reply_address}
              onChange={handleInputChange}
              className="input-field"
              placeholder="info@apro.com.tr"
            />
          </div>
        </div>

        {/* CC ve BCC */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              CC Email
            </label>
            <input
              type="email"
              name="smtp_cc_mail"
              value={formData.smtp_cc_mail}
              onChange={handleInputChange}
              className="input-field"
              placeholder="cc@apro.com.tr"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              BCC Email
            </label>
            <input
              type="email"
              name="smtp_bcc_mail"
              value={formData.smtp_bcc_mail}
              onChange={handleInputChange}
              className="input-field"
              placeholder="bcc@apro.com.tr"
            />
          </div>
        </div>

        {/* Butonlar */}
        <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-600">
          <button
            type="button"
            onClick={() => setShowTestModal(true)}
            className="btn-secondary flex items-center space-x-2"
          >
            <FiSend size={16} />
            <span>Bağlantıyı Test Et</span>
          </button>

          <button
            type="submit"
            disabled={updateMutation.isLoading}
            className="btn-primary flex items-center space-x-2"
          >
            <FiSave size={16} />
            <span>{updateMutation.isLoading ? 'Kaydediliyor...' : 'Kaydet'}</span>
          </button>
        </div>
      </form>

      {/* Test Modal */}
      {showTestModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="modal-header">
              <h3 className="text-lg font-medium text-white">
                SMTP Bağlantı Testi
              </h3>
            </div>
            
            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Test maili göndermek için email adresi girin:
              </p>
              
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="input-field mb-4"
                placeholder="test@example.com"
                required
              />
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowTestModal(false)}
                  className="btn-secondary"
                >
                  İptal
                </button>
                <button
                  onClick={handleTest}
                  disabled={testMutation.isLoading}
                  className="btn-primary"
                >
                  {testMutation.isLoading ? 'Gönderiliyor...' : 'Test Et'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
